import {
  cn,
  healthAttentionIconTileClass,
  healthAttentionRowClass,
  healthCriticalIconTileClass,
  healthCriticalRowClass,
  healthOkIconSoftClass,
  healthOkRowClass,
} from '@up2stay/up2promo-ui';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import type { DashboardHealthStatus } from './dashboard-status-types';

export const dashboardSpringSoft = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 32,
};

export const dashboardSpringSnappy = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 34,
};

/** Profiles SoT section title (`--t-h3`). */
export const boardSectionTitleClass =
  'text-[15.5px] font-bold leading-tight text-foreground';

/** Quiet meta / supporting copy under section titles. */
export const boardSectionMetaClass =
  'text-[13.5px] leading-relaxed text-muted-foreground';

export function statusBadgeVariant(
  status: DashboardHealthStatus,
): 'destructive' | 'warning' | 'success' | 'secondary' {
  switch (status) {
    case 'critical':
    case 'sync':
      return 'destructive';
    case 'warning':
    case 'setup':
      return 'warning';
    case 'ok':
      return 'success';
    default:
      return 'secondary';
  }
}

export function healthRowTone(status: DashboardHealthStatus): string {
  switch (status) {
    case 'critical':
    case 'sync':
      return healthCriticalRowClass;
    case 'warning':
    case 'setup':
      return healthAttentionRowClass;
    case 'ok':
      return healthOkRowClass;
    default:
      return 'hover:bg-muted/60';
  }
}

export function healthIconTileClass(status: DashboardHealthStatus): string {
  switch (status) {
    case 'critical':
    case 'sync':
      return healthCriticalIconTileClass;
    case 'warning':
    case 'setup':
      return healthAttentionIconTileClass;
    case 'ok':
      return healthOkIconSoftClass;
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function BoardSectionHeader({
  id,
  title,
  titleEnd,
  action,
  meta,
  className,
}: {
  id: string;
  title: string;
  titleEnd?: ReactNode;
  action?: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-3 flex flex-wrap items-end justify-between gap-2',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 id={id} className={boardSectionTitleClass}>
            {title}
          </h2>
          {titleEnd}
        </div>
        {meta ? (
          <div className={cn('mt-1', boardSectionMetaClass)}>{meta}</div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function BoardRow({
  onClick,
  children,
  className,
  highlighted,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
  highlighted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full min-h-11 cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        highlighted &&
          'bg-foreground/[0.06] ring-1 ring-inset ring-foreground/15',
        className,
      )}
    >
      {children}
      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}
